<script setup lang="ts">
import { DownArrowIcon } from '#shared/components/icons';
import { useVModel } from '@vueuse/core';
import { useAttrs, ref, toRef, onMounted } from 'vue';
import { usePopover } from '#shared/components/ui/AppPopover/usePopover';
import AppPopoverBody from '#shared/components/ui/AppPopover/AppPopoverBody.vue';

const props = withDefaults(
  defineProps<{
    items: string[];
    variant?: 'white' | 'light';
    placeholder?: string;
    modelValue?: string;
    error?: boolean;
    fluid?: boolean;
    disabled?: boolean;
    autofocus?: boolean;
  }>(),
  {
    items: () => [],
    variant: 'light',
    placeholder: '',
    modelValue: '',
    error: false,
    fluid: false,
    disabled: false,
    autofocus: false,
  }
);
defineEmits<{
  (e: 'update:modelValue', modelValue: string): void;
}>();

defineOptions({ inheritAttrs: false });

const inputRef = ref<HTMLInputElement>();
const text = useVModel(props, 'modelValue', undefined, { passive: true });
const attrs = useAttrs();
const popoverRef = ref<InstanceType<typeof AppPopoverBody>>();
const referenceRef = ref<HTMLElement>();
const { isVisible: isPopoverVisible, popoverStyles } = usePopover(
  { popover: toRef(() => popoverRef.value?.el), reference: referenceRef },
  {
    placement: 'bottom-start',
    sameWidth: true,
    distance: 5,
    isClosableOnBlur: true,
  }
);

onMounted(() => {
  if (props.autofocus) inputRef.value?.focus();
});
function isSelected(item: string) {
  return text.value === item;
}
function tryToOpen() {
  if (!props.disabled) isPopoverVisible.value = true;
}
function onSelect(item: string) {
  text.value = item;
  isPopoverVisible.value = false;
}
function onInput(event: Event) {
  const target = event.target as HTMLInputElement;
  text.value = target.value ?? '';
}
</script>

<template>
  <div
    ref="referenceRef"
    class="autocomplete"
    :class="[
      { autocomplete_opened: isPopoverVisible },
      { autocomplete_error: props.error },
      { autocomplete_fluid: props.fluid },
      { autocomplete_filled: !!text?.length },
      `autocomplete_variant_${props.variant}`,
    ]"
    tabindex="0"
    v-bind="attrs"
    @click="tryToOpen"
  >
    <div class="autocomplete__control">
      <div class="autocomplete__prepend">
        <slot name="prepend" />
      </div>
      <div class="autocomplete__wrapper">
        <input
          ref="inputRef"
          type="text"
          :value="text"
          :disabled="props.disabled"
          @input="onInput"
        />
        <div class="autocomplete__placeholder">
          {{ props.placeholder }}
        </div>
        <div class="autocomplete__arrow">
          <DownArrowIcon />
        </div>
      </div>
      <div class="autocomplete__append">
        <slot name="append" />
      </div>
    </div>
    <p class="autocomplete__helper-text m-0">
      <slot name="helper-text"></slot>
    </p>
  </div>
  <AppPopoverBody
    ref="popoverRef"
    v-model="isPopoverVisible"
    :padding="0"
    :style="popoverStyles"
  >
    <div
      v-for="item in props.items"
      :key="item"
      class="autocomplete__item"
      :class="{ autocomplete__item_selected: isSelected(item) }"
      @click="onSelect(item)"
    >
      <slot v-bind="{ item }" name="item">
        {{ item }}
      </slot>
    </div>
  </AppPopoverBody>
</template>

<style scoped lang="scss">
.autocomplete {
  &__control {
    display: flex;
    align-items: stretch;
    flex-wrap: nowrap;
    height: 52px;
    line-height: 22px;
    border: 2px solid transparent;
    border-radius: 12px;
    transition: all 0.15s ease-in-out;
  }

  &__wrapper {
    position: relative;
    flex: 1;
  }

  input {
    appearance: none;
    background: transparent;
    margin: 0;
    width: 100%;
    height: 100%;
    padding: var(--app-spacing-4) var(--app-spacing-7) 0 var(--app-spacing-3);
    border: none;
    outline: none;
    min-width: 0;
    font-size: 15px;
    line-height: 24px;
    font-weight: 500;
    vertical-align: bottom;

    &:read-only {
      cursor: pointer;
    }
  }

  &__placeholder {
    position: absolute;
    font-size: 15px;
    line-height: 20px;
    color: $secondary;
    top: 14px;
    left: 12px;
    pointer-events: none;
    white-space: nowrap;
    overflow: hidden;
    max-width: 75%;
    transition: all 0.15s ease-in-out;
  }

  &_opened,
  &_filled {
    .autocomplete__placeholder {
      font-size: 13px;
      line-height: 16px;
      top: 8px;
      left: 12px;
      background-color: $light;
    }
  }

  &_variant {
    &_light .autocomplete__control {
      background: var(--app-color-white-secondary);
    }

    &_white .autocomplete__control {
      background: var(--app-color-white);
    }
  }

  &_fluid {
    width: 100%;
  }

  &:focus-within &__control {
    border-color: var(--app-color-primary);
  }

  &__arrow {
    position: absolute;
    top: 10px;
    right: 12px;
    width: 24px;
    height: 24px;
    color: var(--app-color-black-secondary);
    pointer-events: none;
    transition: all 0.15s ease-in-out;
  }

  &_opened &__arrow {
    transform: rotate(180deg);
  }

  &__item {
    padding: 1em;
    position: relative;
    cursor: pointer;

    &:hover,
    &_selected {
      background: rgba(0, 0, 0, 5%);
    }

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      height: 1px;
      left: 1em;
      right: 1em;
      bottom: 0;
      background: #e5e5e5;
    }
  }

  &__prepend,
  &__append {
    align-self: center;
    color: var(--app-color-black-secondary);
  }

  &__prepend:not(:empty) {
    margin-left: 0.8em;
  }

  &__append:not(:empty) {
    margin-right: 0.8em;
  }

  &_error &__control {
    border-color: var(--app-color-red);
  }

  &_error &__helper-text {
    color: var(--app-color-red);
  }
}
</style>
